import { useEffect, useMemo, useRef, useState } from 'react';

import useDelay from '@exonet/react-shared/lib/Form/hooks/useDelay';

import parseCollection from 'app/client/modules/Search/config/parseCollection';
import useTabNavigation from 'app/client/modules/Search/hooks/useTabNavigation';

import useDialogBehavior from './useDialogBehavior';
import useSearchRequest from './useSearchRequest';
import useResultNavigation from './useResultNavigation';
import { DEFAULT_TAB_KEY } from '../config/constants';

const useSearch = ({
  t,
  search,
  customerId,
  isOpen,
  onClose,
  initialQuery,
  indexKeys,
  tabKeys,
}) => {
  const dialogElementRef = useRef(null);
  const searchInputRef = useRef(null);

  const [activeTabKey, setActiveTabKey] = useState(DEFAULT_TAB_KEY);
  const [query, setQuery] = useState(initialQuery ?? '');

  const delayedQuery = useDelay(query, 250);

  const { moveLeft, moveRight } = useTabNavigation({
    tabKeys,
    activeTabKey,
    setActiveTabKey,
  });

  const { collection, isSearching, hasSearched, runSearch, resetSearchState } = useSearchRequest({
    isOpen,
    delayedQuery,
    activeTabKey,
    customerId,
    search,
    indexKeys,
  });

  const groupsFromCollection = useMemo(
    () => (collection ? parseCollection(t, collection) : []),
    [t, collection],
  );

  const visibleGroups = useMemo(
    () =>
      activeTabKey === DEFAULT_TAB_KEY
        ? groupsFromCollection
        : groupsFromCollection.filter(({ groupKey }) => groupKey === activeTabKey),
    [activeTabKey, groupsFromCollection],
  );

  const {
    activeResultIndex,
    resetActiveResult,
    setRowElementByIndex,
    moveDown,
    moveUp,
    openActive,
  } = useResultNavigation({ searchInputRef, visibleGroups, onClose });

  useDialogBehavior({
    dialogElementRef,
    searchInputRef,
    isOpen,
    onClose,
    onAfterOpen: () => resetActiveResult(),
    onArrowDown: () => moveDown(),
    onArrowUp: () => moveUp(),
    onArrowLeft: () => {
      resetActiveResult();
      moveLeft();
    },
    onArrowRight: () => {
      resetActiveResult();
      moveRight();
    },
    onEnter: (event) => openActive(event),
  });

  useEffect(() => {
    if (!isOpen) return;
    setActiveTabKey(DEFAULT_TAB_KEY);
  }, [isOpen]);

  useEffect(() => {
    setQuery(initialQuery ?? '');
  }, [initialQuery]);

  useEffect(() => {
    if (!isOpen) return;

    const trimmedQuery = (delayedQuery ?? '').trim();

    if (trimmedQuery.length === 0) {
      resetSearchState();
      resetActiveResult();
      return;
    }

    resetActiveResult();
    runSearch(trimmedQuery);
  }, [delayedQuery, isOpen, activeTabKey, runSearch, resetSearchState, resetActiveResult]);

  return {
    refs: {
      dialogElementRef,
      searchInputRef,
    },

    state: {
      activeTabKey,
      setActiveTabKey,
      query,
      setQuery,
      isSearching,
      hasSearched,
    },

    results: {
      visibleGroups,
      activeResultIndex,
      setRowElementByIndex,
    },
  };
};

export default useSearch;
